// C3 — semantic type-on-hover.
//
// Given a buffer + a hover offset + the schema-bus snapshot, resolve EITHER:
//   - the column reference under the cursor (`col`, `alias.col`, `t.col`,
//     `schema.t.col`) to its owning snapshot column → show the declared data
//     type + coarse category; OR
//   - a completed function call under the cursor (`UPPER(x)`, `my_total(a)`)
//     to its inferred coarse result type via `inferExpressionResultType`.
//
// Pure given its inputs — no Monaco, no I/O. The SqlMonaco hover provider maps
// the returned `SqlRange` to a Monaco range and renders the markdown. Returns
// null whenever nothing can be said with confidence (→ Monaco falls through to
// the LSP / table-card hover providers, which compose, never clobber).
//
// Reuses the SAME machinery the resolvers + type-check collector use, so a
// hover never disagrees with the operator suggester or the diagnostics:
//   - `deriveCompletionContext` for alias bindings + the enclosing statement.
//   - `resolveColumnRefToTable` for alias-aware column resolution.
//   - `inferExpressionResultType` (+ a routine resolver) for expression typing.

import type { DatabaseEngineId } from '@/core/engines/engineTypes';
import type { TableMeta, RoutineMeta } from '@/core/engines/intellisenseTypes';
import { buildLineIndex, rangeFromOffsets } from '@/features/sql-grammar/document/lineIndex';
import type { ParseOptions } from '@/features/sql-grammar/model';
import { columnSqlTypeFromDataType } from '@/features/sql-resolver/json/operators/columnSqlType';
import { findDottedChainStart, parseDottedChain } from '@/features/sql-resolver/dotted/dottedParts';
import { inferExpressionResultType } from '@/features/sql-resolver/structure/inferExpressionResultType';
import type { OperandLiteralSqlType, SqlRange } from '@/features/sql-resolver/types';

import { deriveCompletionContext } from '../context/deriveContext';
import { resolveColumnRefToTable } from '../context/resolveColumnRefToTable';
import { extendCursorToIdentEnd } from '../../navigational-features/go-to/cursorIdent';
import { makeRoutineReturnResolver } from '../operators/routineReturnType';
import { databaseEngineIdToSqlEngine } from '../versionGating/engineIdMapping';

export type SemanticHoverInfo =
  | {
      readonly kind: 'column';
      readonly columnName: string;
      readonly tableName: string;
      readonly dataType: string;
      readonly category: OperandLiteralSqlType;
      readonly range: SqlRange;
    }
  | {
      readonly kind: 'expression';
      readonly category: OperandLiteralSqlType;
      readonly range: SqlRange;
    };

export type ResolveSemanticHoverInput = {
  readonly source: string;
  readonly offset: number;
  readonly engineId: DatabaseEngineId;
  readonly snapshotTables: readonly TableMeta[];
  readonly snapshotRoutines?: readonly RoutineMeta[];
};

export function resolveSemanticHover(input: ResolveSemanticHoverInput): SemanticHoverInfo | null {
  const { source, offset, engineId, snapshotTables } = input;
  if (!source.trim()) return null;

  const sqlEngine = databaseEngineIdToSqlEngine(engineId) ?? 'postgres';
  const fold = engineId === 'oracle' ? toUpper : toLower;
  const options: ParseOptions = { engine: sqlEngine, identifierFolding: foldingFor(engineId) };

  // Identify the dotted identifier chain straddling the cursor.
  const cursorAtEnd = extendCursorToIdentEnd(source, offset);
  const chainStart = findDottedChainStart(source, cursorAtEnd, options);
  const chain = parseDottedChain(source, chainStart, cursorAtEnd, options);
  if (!chain || chain.parts.length === 0) return null;

  const lastPart = chain.parts[chain.parts.length - 1]!;
  const chainEnd = lastPart.range.endOffset;

  // A single identifier immediately followed by `(` is a function call, never a
  // column reference — type it as an expression.
  if (chain.parts.length === 1 && nextNonSpaceIsOpenParen(source, chainEnd)) {
    return expressionHover(source, chainStart, chainEnd, sqlEngine, input.snapshotRoutines, fold);
  }

  // Otherwise, try to resolve it as a column against the in-scope relations.
  const ctx = deriveCompletionContext(source, offset, engineId);
  const ref = chain.parts.map((p) => p.value).join('.');
  const resolved = resolveColumnRefToTable<TableMeta>(ref, ctx.aliasBindings, snapshotTables, fold);
  if (!resolved) return null;

  return {
    kind: 'column',
    columnName: resolved.column.name,
    tableName: resolved.table.name,
    dataType: resolved.column.dataType,
    category: columnSqlTypeFromDataType(resolved.column.dataType),
    range: lastPart.range,
  };
}

function expressionHover(
  source: string,
  chainStart: number,
  chainEnd: number,
  sqlEngine: ParseOptions['engine'],
  routines: readonly RoutineMeta[] | undefined,
  fold: (s: string) => string,
): SemanticHoverInfo | null {
  const openIdx = source.indexOf('(', chainEnd);
  if (openIdx < 0) return null;
  const closeIdx = matchingCloseParen(source, openIdx);
  if (closeIdx < 0) return null;

  const exprText = source.slice(chainStart, closeIdx + 1);
  const resolveRoutine = makeRoutineReturnResolver(routines ?? [], fold);
  const category = inferExpressionResultType(exprText, sqlEngine, resolveRoutine);
  if (category === 'unknown') return null;

  const lineIdx = buildLineIndex(source);
  return { kind: 'expression', category, range: rangeFromOffsets(lineIdx, chainStart, closeIdx + 1) };
}

// True when the next non-whitespace char after `from` is `(`.
function nextNonSpaceIsOpenParen(source: string, from: number): boolean {
  let i = from;
  while (i < source.length && /\s/.test(source[i]!)) i += 1;
  return source[i] === '(';
}

// Index of the `)` that closes the `(` at `openIdx`, skipping string literals;
// −1 when unbalanced.
function matchingCloseParen(source: string, openIdx: number): number {
  let depth = 0;
  let inStr: string | null = null;
  for (let i = openIdx; i < source.length; i += 1) {
    const ch = source[i]!;
    if (inStr) {
      if (ch === inStr) inStr = null;
      continue;
    }
    if (ch === "'" || ch === '"' || ch === '`') { inStr = ch; continue; }
    if (ch === '(') depth += 1;
    else if (ch === ')') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
}

const toLower = (s: string): string => s.toLowerCase();
const toUpper = (s: string): string => s.toUpperCase();

function foldingFor(engineId: DatabaseEngineId): ParseOptions['identifierFolding'] {
  if (engineId === 'pgsql') return 'lower';
  if (engineId === 'oracle') return 'upper';
  return 'case-insensitive';
}
